/**
 * Draws the controller's ghost dot and fading trail over the transcript column.
 *
 * Canvas rather than DOM nodes: this repaints every frame while a finger is
 * down, and the trail is up to 48 points. Repositioning that many elements per
 * frame is what makes a trace feel laggy.
 */
import { useEffect, useRef } from 'react';
import type { SegmentBox } from '../lib/scroll-anchor.js';
import { projectPoint, type TouchTrace } from '../lib/touch-trace.js';

export interface TouchOverlayProps {
  trace: TouchTrace;
  /** Read fresh each frame: both change as the transcript grows and scrolls. */
  geometry: () => { boxes: SegmentBox[]; scrollTop: number; width: number; height: number } | null;
  /** The trail colour. Matches the controller's accent so its source is obvious. */
  color?: string;
}

export function TouchOverlay({ trace, geometry, color = '#f0a' }: TouchOverlayProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let frame = 0;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      const geom = geometry();
      if (!canvas || !geom) return;

      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.round(geom.width));
      const height = Math.max(1, Math.round(geom.height));
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const now = Date.now();
      trace.prune(now);
      const points = trace.points(now);
      if (points.length === 0) return;

      const projected = points
        .map((point) => projectPoint(point, { width, height }, geom.boxes, { scrollTop: geom.scrollTop }, now))
        .filter((p): p is NonNullable<typeof p> => p !== null);
      if (projected.length === 0) return;

      // The trail: newest first, so the stroke fades as it goes back in time.
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let i = 1; i < projected.length; i += 1) {
        const from = projected[i - 1]!;
        const to = projected[i]!;
        ctx.globalAlpha = Math.min(from.opacity, to.opacity) * 0.55;
        ctx.strokeStyle = color;
        ctx.lineWidth = 3 + 5 * to.opacity;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }

      // The ghost dot, only while the finger is actually down.
      if (trace.isDown) {
        const head = projected[0]!;
        ctx.globalAlpha = 0.25;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(head.x, head.y, 22, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.arc(head.x, head.y, 8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [trace, geometry, color]);

  // Pointer-events none: the overlay shows where someone else is pointing and
  // must never swallow this viewer's own scroll.
  return <canvas className="touch-overlay" ref={canvasRef} aria-hidden="true" />;
}
